import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Download, ArrowRight } from "lucide-react";
import SEO from "@/components/common/SEO";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import MarketOpportunityChart from "@/components/research/MarketOpportunityChart";
import GovernanceCrisisSection from "@/components/research/GovernanceCrisisSection";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ReportRequestDialog from "@/components/research/ReportRequestDialog";
import MainLayout from "@/components/layout/MainLayout";

/* -------------------- UI HELPERS -------------------- */

const Pill = ({ children }: { children: React.ReactNode }) => (
  <div className="inline-flex items-center gap-2 px-3 py-1 text-sm font-medium border rounded-full bg-card text-muted-foreground">
    {children}
  </div>
);

const Stat = ({ value, label }: { value: string; label: string }) => (
  <div className="p-4 border border-dashed rounded-lg border-border text-muted-foreground">
    <strong className="block text-2xl font-bold text-foreground">{value}</strong>
    <span>{label}</span>
  </div>
);

const FeatureCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Card className="bg-card/50">
    <CardHeader>
      <CardTitle className="text-lg">{title}</CardTitle>
    </CardHeader>
    <CardContent>
      <p className="text-muted-foreground">{children}</p>
    </CardContent>
  </Card>
);

const Callout = ({ children }: { children: React.ReactNode }) => (
  <div className="p-6 border rounded-lg border-primary/30 bg-primary/5 text-primary-foreground/80">
    <p className="text-lg italic text-center text-foreground">{children}</p>
  </div>
);

const Section = ({
  id,
  pill,
  title,
  kicker,
  children,
  className = "",
}: {
  id: string;
  pill: string;
  title: string;
  kicker: string;
  children: React.ReactNode;
  className?: string;
}) => {
  const variants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" as const },
    },
  };

  return (
    <motion.section
      id={id}
      variants={variants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className={`py-24 sm:py-32 ${className}`}
    >
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center text-center gap-4">
          <Pill>{pill}</Pill>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight max-w-3xl">
            {title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl">
            {kicker}
          </p>
          <div className="w-full mt-8">{children}</div>
        </div>
      </div>
    </motion.section>
  );
};

/* -------------------- PAGE -------------------- */

const ReportsPage = () => {
  const [isReportDialogOpen, setIsReportDialogOpen] = React.useState(false);

  return (
    <MainLayout>
      <SEO
        title="Reports and Publications"
        description="Read CanXP AI research reports and publications on Canadian AI sovereignty, governance, compliance, and market opportunity."
        keywords="CanXP AI reports, AI in Canada, Canadian AI publications, digital sovereignty"
        imageUrl="/images/research/canada.jpg"
        url="/reports"
      />

      <div className="bg-background text-foreground">
        <Header />

        {/* HERO */}
        <header className="relative py-24 text-center sm:py-32 lg:py-40 hero-gradient">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: `url('/images/research/canada.jpg')` }}
          />

          <div className="container relative z-10 mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="flex flex-col items-center gap-6"
            >
              <Pill>Reports & Publications</Pill>

              <h1 className="text-4xl font-extrabold tracking-tighter sm:text-5xl lg:text-6xl max-w-4xl">
                The State of AI in Canada 2025
              </h1>

              <p className="max-w-3xl mx-auto text-lg text-muted-foreground sm:text-xl">
                Sovereignty, compliance, and trust are at stake.
              </p>

              <div className="flex flex-wrap items-center justify-center gap-4 mt-4">
                {/* ✅ MUST be type="button" */}
                <Button
                  type="button"
                  size="lg"
                  className="px-8 py-6 text-lg rounded-full"
                  onClick={() => setIsReportDialogOpen(true)}
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download Full Report
                </Button>

                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="px-8 py-6 text-lg rounded-full"
                >
                  <Link to="/auth">
                    Join the Movement <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
              </div>

              <div className="grid w-full max-w-5xl grid-cols-1 gap-4 mt-8 sm:grid-cols-3">
                <Stat value="+97%" label="report productivity gains with AI" />
                <Stat value="$308K" label="added per breach from shadow AI" />
                <Stat value="$28B" label="Canadian AI market by 2028" />
              </div>
            </motion.div>
          </div>
        </header>

        <main>
          <Section
            id="crisis"
            pill="The Crisis"
            title="The AI Governance Gap"
            kicker="AI adoption has exploded—but governance hasn't."
            className="bg-card/30"
          >
            <GovernanceCrisisSection />
          </Section>

          <Section
            id="opportunity"
            pill="The Opportunity"
            title="A $28 Billion Canadian AI Market"
            kicker="Canada has a generational opportunity."
          >
            <MarketOpportunityChart />
          </Section>

          <Section
            id="solution"
            pill="Our Solution"
            title="CanXP Sovereign AI"
            kicker="Everything people love about AI—without the risk."
            className="bg-card/30"
          >
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <FeatureCard title="Data Sovereignty">
                All data stays in Canada.
              </FeatureCard>
              <FeatureCard title="Compliance by Design">
                Built for PIPEDA and enterprise controls.
              </FeatureCard>
              <FeatureCard title="Canadian Context">
                Bilingual, culturally aware, locally trained.
              </FeatureCard>
            </div>

            <div className="mt-12">
              <Callout>
                "Canada's future depends on a strong AI strategy."
              </Callout>
            </div>
          </Section>
        </main>

        <footer className="py-20 text-center bg-primary text-primary-foreground">
          <div className="container flex flex-col items-center gap-6 mx-auto px-4">
            <h2 className="text-3xl font-bold sm:text-4xl">
              Ready to Dive Deeper?
            </h2>

            {/* ✅ MUST be type="button" */}
            <Button
              type="button"
              size="lg"
              variant="secondary"
              className="px-8 py-6 text-lg rounded-full"
              onClick={() => setIsReportDialogOpen(true)}
            >
              <Download className="w-5 h-5 mr-2" />
              Download Full Report
            </Button>
          </div>
        </footer>
      </div>

      {/* ✅ ACCESSIBLE, WORKING DIALOG */}
      <Dialog open={isReportDialogOpen} onOpenChange={setIsReportDialogOpen}>
        <ReportRequestDialog setOpen={setIsReportDialogOpen} />
      </Dialog>
    </MainLayout>
  );
};

export default ReportsPage;